import type { ReactNode } from "react";
import { Label } from "@/components/ui/Input";
import { HubTouchSelect } from "@/components/ui/HubTouchSelect";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { useQuery } from "@tanstack/react-query";

import { zplPrintersQueryOptions, zplStationsQueryOptions } from "@/lib/zplPrintLocation";
import { formatStationDocumentCoverage, formatStationLabelStockDetail, type ZplPrintDocument } from "@/lib/zplPrintLocationMatch";
import { cn } from "@/lib/utils";

export const ZPL_LOCATION_SELECT_HOVER_HINT =
  "Pick a station. Hub sends the label to the printer at that station with the right stock and DPI.";

export function ZplLocationSelect({
  id,
  label = "Printer",
  showLabel = true,
  size = "default",
  value,
  onChange,
  disabled = false,
  className,
  triggerClassName,
  triggerMode = "labeled",
  triggerIcon,
  ariaSummary,
  onListOpenChange,
  unavailableMessage,
  hoverHint = ZPL_LOCATION_SELECT_HOVER_HINT,
  siteCode,
  documents,
}: {
  id: string;
  label?: string;
  showLabel?: boolean;
  size?: "compact" | "default" | "touch";
  value: string;
  onChange: (station: string) => void;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  triggerMode?: "labeled" | "icon";
  triggerIcon?: ReactNode;
  ariaSummary?: string;
  onListOpenChange?: (open: boolean) => void;
  unavailableMessage?: string | null;
  hoverHint?: string;
  /** When set, only stations at this site are listed. The current value stays visible. */
  siteCode?: string;
  /** Documents this page can send. The detail line becomes "All" or the covered names. */
  documents: readonly ZplPrintDocument[];
}): JSX.Element {
  const stationsQuery = useQuery(zplStationsQueryOptions());
  const printersQuery = useQuery(zplPrintersQueryOptions());
  const printers = printersQuery.data?.printers ?? [];
  const catalogPending = stationsQuery.data == null || (documents.length > 0 && printersQuery.data == null);
  const stations = (stationsQuery.data?.stations ?? []).filter((station) => {
    const onSite =
      siteCode == null || siteCode === "" || station.siteCode === siteCode || String(station.id) === value;
    if (!onSite) return false;
    if (documents.length === 0) {
      return formatStationLabelStockDetail(station.id, printers) != null || String(station.id) === value;
    }
    return formatStationDocumentCoverage(station.id, printers, documents) != null || String(station.id) === value;
  });
  const nameCounts = new Map<string, number>();
  for (const station of stations) {
    nameCounts.set(station.name, (nameCounts.get(station.name) ?? 0) + 1);
  }

  const labelClassName =
    size === "touch"
      ? "text-[10px] font-semibold uppercase tracking-wide text-slate-500"
      : undefined;

  return (
    <div className={cn("flex flex-col items-stretch gap-0.5", className)}>
      {showLabel ? (
        <div className="flex items-center gap-1">
          <Label htmlFor={id} className={labelClassName}>
            {label}
          </Label>
          {hoverHint !== "" ? <HoverHintWithGlyph hoverHint={hoverHint} /> : null}
        </div>
      ) : null}
      <HubTouchSelect
        id={id}
        aria-label={showLabel ? undefined : ariaSummary?.trim() !== "" ? ariaSummary : label}
        size={size}
        value={catalogPending ? "" : value}
        onChange={onChange}
        placeholder="Printer"
        options={stations.map((station) => ({
          value: String(station.id),
          label:
            (nameCounts.get(station.name) ?? 0) > 1 && station.siteCode
              ? `${station.name} (${station.siteCode})`
              : station.name,
          detail:
            (documents.length > 0
              ? formatStationDocumentCoverage(station.id, printers, documents)
              : formatStationLabelStockDetail(station.id, printers)) ?? undefined,
        }))}
        disabled={disabled || catalogPending}
        triggerClassName={triggerClassName}
        triggerMode={triggerMode}
        triggerIcon={triggerIcon}
        onOpenChange={onListOpenChange}
      />
      {unavailableMessage != null && unavailableMessage !== "" ? (
        <p className="text-xs text-rose-700">{unavailableMessage}</p>
      ) : null}
    </div>
  );
}
